import { useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useConfirm } from '../../lib/confirm'
import { formatDate, titleCase } from '../../lib/format'
import type { CourseSummary, Organization, RoleCoverage } from '../../types'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Select, Skeleton, StatusBadge,
} from '../../components/ui'
import { CheckIcon } from '../../components/icons'

interface OrgMember { uid: string; role?: string; roles?: string[]; status?: string; joined_at?: string }
interface TrainingProgram { course_id: string; is_mandatory?: boolean; assigned_at?: string }

export default function OrgDetail() {
  const { orgId } = useParams<{ orgId: string }>()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [courseToAssign, setCourseToAssign] = useState('')

  const orgQuery = useQuery({
    queryKey: ['gov', 'org', orgId],
    queryFn: () => api.get<{ organization: Organization; members: OrgMember[] }>(`/organizations/${orgId}`),
    enabled: Boolean(orgId),
  })
  const coverageQuery = useQuery({
    queryKey: ['gov', 'org-coverage', orgId],
    // GET .../role-coverage wraps the report as {ok, coverage} - reading the
    // fields straight off the response (rather than off .coverage) meant this
    // panel always showed "0 of 0" with an empty table.
    queryFn: () => api.get<{ ok: boolean; coverage: RoleCoverage }>(`/organizations/${orgId}/role-coverage`),
    enabled: Boolean(orgId) && orgQuery.data?.organization.type === 'cooperative',
  })
  const trainingQuery = useQuery({
    queryKey: ['gov', 'org-training', orgId],
    queryFn: () => api.get<{ ok: boolean; programs: TrainingProgram[] }>(`/gov/organizations/${orgId}/training`),
    enabled: Boolean(orgId),
  })
  const coursesQuery = useQuery({
    queryKey: ['learning', 'courses'],
    queryFn: () => api.get<{ ok: boolean; courses: CourseSummary[] }>('/learning/courses'),
  })

  const assignTraining = useMutation({
    mutationFn: (course_id: string) => api.post(`/gov/organizations/${orgId}/training`, { course_id, is_mandatory: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov', 'org-training', orgId] })
      setCourseToAssign('')
    },
  })
  const removeTraining = useMutation({
    mutationFn: (course_id: string) => api.del(`/gov/organizations/${orgId}/training/${course_id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['gov', 'org-training', orgId] }),
  })

  if (!orgId) return <Navigate to="/users" replace />
  if (orgQuery.isError) {
    return (
      <Alert tone="danger">
        {(orgQuery.error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/users">Back to organisations</Link></div>
      </Alert>
    )
  }
  if (orgQuery.isLoading || !orgQuery.data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  const { organization: org, members } = orgQuery.data
  const courses = coursesQuery.data?.courses ?? []
  const programs = trainingQuery.data?.programs ?? []
  const assignedIds = new Set(programs.map((p) => p.course_id))

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{org.name}</h1>
          <p className="page-head__sub">
            {titleCase(org.type)} · {[titleCase(org.district_id), titleCase(org.region_id)].filter(Boolean).join(', ')}
          </p>
        </div>
        <StatusBadge status={org.status} />
      </div>

      <div className="grid grid--tiles-2" style={{ marginBottom: 'var(--sp-6)' }}>
        <Card>
          <CardBody>
            <div className="stat__label">Members</div>
            <div className="stat__value" style={{ fontSize: 'var(--text-lg)' }}>{members.length}</div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="stat__label">Registration</div>
            <div style={{ fontWeight: 550 }}>{org.registration_no || '—'}</div>
          </CardBody>
        </Card>
      </div>

      {org.type === 'cooperative' && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader
            title="Governance coverage"
            sub={coverageQuery.data?.coverage ? `${coverageQuery.data.coverage.mandatory_filled ?? 0} of ${coverageQuery.data.coverage.mandatory_total ?? 0} mandatory seats filled` : undefined}
          />
          <CardBody flush>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Role</th><th>Requirement</th><th>Filled</th></tr></thead>
                <tbody>
                  {(coverageQuery.data?.coverage?.rows ?? []).map((r) => (
                    <tr key={r.key}>
                      <td>{r.title}</td>
                      <td><Badge tone={r.requirement === 'mandatory' ? 'warning' : 'neutral'}>{r.requirement}</Badge></td>
                      <td>
                        {r.filled ? (
                          <span style={{ color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <CheckIcon size={14} /> {r.assignee_count}/{r.min_count}
                          </span>
                        ) : `${r.assignee_count ?? 0}/${r.min_count}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardHeader title="Members" />
        <CardBody flush>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Member</th><th>Role</th><th>Status</th><th>Joined</th></tr></thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.uid}>
                    <td><Link to={`/users/${m.uid}`}>{m.uid}</Link></td>
                    <td>{titleCase(m.role)}</td>
                    <td><StatusBadge status={m.status} /></td>
                    <td>{formatDate(m.joined_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Assigned training" sub="Courses mandated for this organisation's members." />
        <CardBody>
          {assignTraining.isError && (
            <div style={{ marginBottom: 'var(--sp-3)' }}>
              <Alert tone="danger">
                {assignTraining.error instanceof ApiError ? assignTraining.error.message : 'Could not assign that course.'}
              </Alert>
            </div>
          )}
          <div className="stack" style={{ marginBottom: 'var(--sp-4)' }}>
            {programs.length === 0 ? (
              <p className="page-head__sub" style={{ margin: 0 }}>No training assigned yet.</p>
            ) : (
              programs.map((p) => {
                const course = courses.find((c) => c.course_id === p.course_id)
                return (
                  <div key={p.course_id} className="row" style={{ justifyContent: 'space-between' }}>
                    <span>{course?.course_name || p.course_id} {p.is_mandatory && <Badge tone="warning">Mandatory</Badge>}</span>
                    <Button size="sm" variant="ghost" onClick={async () => {
                      if (await confirm({
                        title: 'Remove this required course?',
                        message: course?.course_name || p.course_id,
                        destructive: true,
                      })) removeTraining.mutate(p.course_id)
                    }}>Remove</Button>
                  </div>
                )
              })
            )}
          </div>
          <div className="row" style={{ alignItems: 'flex-end', gap: 'var(--sp-3)' }}>
            <div style={{ flex: 1 }}>
              <Select label="Assign a course" value={courseToAssign} onChange={(e) => setCourseToAssign(e.target.value)}>
                <option value="">Select…</option>
                {courses.filter((c) => !assignedIds.has(c.course_id)).map((c) => (
                  <option key={c.course_id} value={c.course_id}>{c.course_name}</option>
                ))}
              </Select>
            </div>
            <Button
              loading={assignTraining.isPending}
              disabled={!courseToAssign}
              onClick={() => assignTraining.mutate(courseToAssign)}
            >
              Assign
            </Button>
          </div>
        </CardBody>
      </Card>
    </>
  )
}
